import React, { useEffect, useState, useRef, Children } from "react";
import {
  useNavigate,
  useLocation,
  RouteProps,
  Outlet,
  useSearchParams,
  redirect,
} from "react-router-dom";

import Sdk from "api.digitalpages.module.sdk.api";
import { userSlice, commonSlice, contentSlice } from "./store/reducers";
import { RootState, useAppDispatch } from "./store";
import { WModal } from "./components/WModal";
import { GlobalFonts, Loading } from "senac-components";
import { useSelector } from "react-redux";
import userDataSlice, {
  downloadedContents,
  loadPlayerObserver,
} from "./store/reducers/userDataSlice";
import { signinAnonymous } from "./store/reducers/userSlice";
import { WModalMigration } from "./components/WModalMigration";
import Clarity from "@microsoft/clarity";

const App: React.FC<RouteProps> = ({ path }) => {
  const isWeb = window.innerWidth > 1024;
  const effectRan = useRef(false);
  const [initialized, setInitialized] = useState(false);
  const route = useSelector<RootState>((p) => p.commonState.route);
  const contentOptions = useSelector<RootState>(
    (state) => state.contentState.contentOption
  );
  const activeUser = useSelector<RootState>(
    (state) => state.userState.user != undefined
  );
  const inMobile = useSelector<RootState>(
    (state) => state.commonState.runningInMobile
  );
  const withInternet = useSelector<RootState>(
    (state) => state.commonState.withInternet
  );
  const hasSummaryState = useSelector<RootState>(
    (state) => state.contentState.summaryItem
  );

  const userState = useSelector<RootState>(
    (state) => state.userState
  ) as RootState["userState"];
  const loader = useSelector<RootState>((state) => state.commonState.loader);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authenticated = Sdk.Api.authorization.authenticated;

  const urlParams = Object.fromEntries(searchParams);
  const ssoRoute = location.pathname.includes("sso/");

  useEffect(() => {
    if (inMobile) return;
    dispatch(commonSlice.actions.setRefferer());
    const path = window.location.hash;
    const shouldHaveContentReference =
      path.includes(`player`) ||
      path.includes(`content/uid`) ||
      path.includes(`library/readinglist/uid`);

    if (!shouldHaveContentReference) {
      dispatch(contentSlice.actions.activeReference(null));
    }

    if (!path.includes("player")) {
      dispatch(contentSlice.actions.updateContentAnchor(undefined));
    }
  }, [location]);

  useEffect(()=>
  {
    updateInsightsInfo();
  },[userState.user, initialized])

  useEffect(() => {
    ///evita executar aplicação duas vezes
    /// https://www.youtube.com/watch?v=81faZzp18NM
    if (effectRan.current === false) {
      initInsights();
      loadConfig();
      return () => {
        effectRan.current = true;
      };
    }
  }, []);

  const pagesNoRedirect = ["recover-password", "login", "register"];

  const checkRedirect = () => {
    return !pagesNoRedirect.some((item) => location.pathname.includes(item));
  };

  useEffect(() => {
    if (!ssoRoute) {
      dispatch(userSlice.actions.setSsoData(undefined));
      dispatch(userSlice.actions.setSsoLoading(undefined));
    }
    const code = searchParams.get("importCode");

    dispatch(userSlice.actions.setImportReadingList(code ?? undefined));

    window.addEventListener("message", receiveMessage);
    document.addEventListener("visibilitychange", changeDocumentVisivity);

    return () => {
      window.removeEventListener("message", receiveMessage);
      window.removeEventListener("visibilitychange", changeDocumentVisivity);
    };
  }, []);

  useEffect(() => {
    if (
      !activeUser &&
      !Sdk.Api.authorization.authenticated &&
      !userState.ssoParams &&
      route &&
      route !== "/inicio"
    ) {
      navigate("/inicio");
    }
  }, [route]);

  useEffect(() => {
    if (
      !activeUser &&
      !Sdk.Api.authorization.authenticated && 
      !withInternet && initialized
    ) {
        navigate('/user/options');
        return;
      }
      
    if (!withInternet && initialized && location.pathname !== "/offline") {
      navigate("/offline");
      return;
    }

    if (location.pathname === '/offline' && withInternet && initialized) {
      navigate("/inicio");
    }
  }, [withInternet, initialized, location.pathname]);

  useEffect(() => {
    if (!route) return;

    let finalRoute = route as string;

    if (contentOptions === "noredir") return;
    if (contentOptions && String(route).indexOf("/content/uid") === 0) {
      finalRoute = `${route}/${contentOptions}`;
    }

    const exceptions = initialized || userState.ssoParams;

    if (
      location.pathname !== finalRoute &&
      exceptions &&
      !userState.loadingSso
    ) {
      navigate(finalRoute);
      dispatch(commonSlice.actions.changeRoute(undefined));
      dispatch(userSlice.actions.setTokenRecoveryPassword(undefined));

      if (hasSummaryState) {
        dispatch(contentSlice.actions.setSummaryItem(undefined));
      }
    }
  }, [route, contentOptions, userState.ssoParams, userState.loadingSso]);

  const receiveMessage = (e: MessageEvent<any>) => {
    if (e.data.onBack || e.data.userInteraction) {
      dispatch(loadPlayerObserver(e.data));
    }
  };

  const changeDocumentVisivity = (ev:Event) =>
  {
    if (document.visibilityState === "hidden") {
      console.log("Document is hidden");
      dispatch(commonSlice.actions.changeState("idle"));
      // Perform actions when the document is hidden
    } else {
      console.log("Document is visible");
      dispatch(commonSlice.actions.changeState("initializing"));
    }
  }

  const initInsights = ()=>
  {
    Clarity.init("rdejzsdeqw");
  }

  const updateInsightsInfo = ()=>
  {
    if (!initialized) return;

    if (userState?.user)
    {
      Clarity.identify(userState.user.uid, undefined, undefined, userState.user.name);

      for (var project of userState.projects ?? [])
      {
        Clarity.setTag("project", project.name ?? "projeto");
      }

    }
    else
    {
      Clarity.identify("anônimo", undefined, undefined, undefined);
    }
  }

  const loadConfig = async () => {
    const response = await fetch("./api_context.json");
    const config = await response.json();
    console.log(window.location);

    dispatch(loadPlayerObserver());
    dispatch(userSlice.actions.closeModal());
    dispatch(userSlice.actions.changeStep(0));

    if (
      localStorage.getItem("iammobile") == "true" ||
      window.location.href.includes('iammobile=true') || 
      window.location.search.includes('iammobile=true')) {
      console.log("IAMMOBILE");
      document.addEventListener("deviceready", function () {
        setupSdk(config);
        listenAppEvents();
        dispatch(commonSlice.actions.runningInMobile(true));
      });
    } else {
      dispatch(commonSlice.actions.runningInMobile(false));
      // dispatch(commonSlice.actions.runningInMobile(true));
      // dispatch(commonSlice.actions.withInternet(true));
      setupSdk(config);
    }
  };

  const listenAppEvents = async () => {
    var cordovaInstance = (window as any).CordovaExec;

    ///progresso de download
    cordovaInstance(
      function (response: any) {
        console.log("RegisterEditionsChanges", response);
        //todo: melhorar/otimizar
        dispatch(downloadedContents());
      },
      "UnifiedApiPlugin",
      "Process",
      [{ name: "RegisterEditionsChanges" }]
    );

    cordovaInstance(
      function (response: any) {
        console.log("network", response);
        var result = JSON.parse(response);
        dispatch(commonSlice.actions.withInternet(result.status));
        // dispatch(commonSlice.actions.withInternet(false));
      },
      "UnifiedApiPlugin",
      "Process",
      [{ name: "HandleNetwork" }]
    );
  };

  const setupSdk = async (config: any) => {
    dispatch(commonSlice.actions.changeState("idle"));

    const domain: any = `https://${config.domain}`;
    const projectKey = config.project_key || config.pk;
    const apiEnv = config.api_env || config.ae;
    const projectUid = config.project_uid || config.pu;

    Sdk.Api.domain = domain;
    //todo:corrigir sdk
    Sdk.Api.authorization.domain = domain;
    Sdk.Api.storage.domain = domain;
    Sdk.Api.store.domain = domain;
    Sdk.Api.cms.domain = domain;
    Sdk.Api.dam.domain = domain;

    Sdk.Api.setEnviroment(apiEnv);
    Sdk.Api.setProjectKey(projectKey);

    await Sdk.Api.initializeFromProject(projectUid, "Insights_1", "Insights_1");

    // Sdk.Api.authorization.addCredentialListen(async () => {
    //   if (!Sdk.Api.authorization.authenticated && activeUser && !logoutProcessing)
    //   {
    //     setLogoutProcessing(true);
    //     await dispatch(userSlice.actions.logout());

    //     //todo:melhorar
    //     setTimeout(() => {
    //       setLogoutProcessing(false);
    //     }, (3000));
    //   }
    // });

    if (activeUser && !Sdk.Api.authorization.authenticated) {
      await dispatch(userSlice.actions.logout());
    }

    dispatch(commonSlice.actions.changeState("initializing"));

    if (!withInternet) {
      navigate("/offline");
    } else if (location.pathname === "/") {
      navigate("/inicio");
    }

    if (
      !authenticated &&
      !activeUser &&
      checkRedirect() &&
      !userState.loadingSso &&
      !ssoRoute
    ) {
      navigate("/user/options");
    }

    if (location.pathname.includes("recover-password")) {
      dispatch(signinAnonymous(projectUid));
      const token = searchParams.get("token");
      dispatch(userSlice.actions.setTokenRecoveryPassword(token));
    }
    setInitialized(true);
  };

  // return null;
  return (
    <>
      {initialized && <Outlet />}
      {
        <Loading
          show={
            ((!initialized || Boolean(userState?.loadingSso)) && !ssoRoute) ||
            Boolean(userState.loadingSso) ||
            Boolean(loader)
          }
          message={loader ? "Carregando conteúdo..." : undefined}
        />
      }
      {<WModal />}
      <WModalMigration />
    </>
  );
};

export default App;